package dev.lingvo.shared.agent

import dev.lingvo.shared.Role
import dev.lingvo.shared.TokenScope

/**
 * The tools that change nothing.
 *
 * Every description here is the longer and more specific of the two that were being served, which
 * was the local server's in all six overlapping cases: "Release readiness for a project: what is
 * untranslated, awaiting review, carrying QA errors, blocked, or unreviewed machine output — per
 * language, as counts" against "Release readiness per language, as counts". The tool description is
 * the one piece of text a model is guaranteed to read, so the choice was never close.
 */
internal object AgentReadTools {

    val TOOLS: List<AgentToolSpec> = listOf(
        AgentToolSpec(
            name = "list_projects",
            summary = "List the projects this credential can reach.",
            safety = ToolSafety.READ,
            scopes = setOf(TokenScope.STRINGS_READ),
            input = AgentSchema.obj(),
            transports = setOf(Transport.LOCAL, Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "get_project",
            summary = "One project: its source language, its target languages and its files.",
            safety = ToolSafety.READ,
            scopes = setOf(TokenScope.STRINGS_READ),
            input = AgentSchema.obj("project" to PROJECT_ARGUMENT),
            transports = setOf(Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "get_translation_job",
            summary = "Read a translation job's state, progress, rejection samples and credit receipt. " +
                "Does not start, retry, cancel or spend. A RUNNING job with refundedAt is abandoned, " +
                "not still working; cancelledAt indicates cancellation. Rejections are bounded " +
                "samples; rejected is the total count.",
            safety = ToolSafety.READ,
            scopes = setOf(TokenScope.STRINGS_READ),
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "job_id" to AgentSchema.string("Canonical job UUID returned by machine_translate or an existing translation run."),
                required = listOf("job_id"),
            ),
            transports = setOf(Transport.LOCAL, Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "project_status",
            summary = "Release readiness for a project: what is untranslated, awaiting review, " +
                "carrying QA errors, blocked, or unreviewed machine output — per language, as counts.",
            safety = ToolSafety.READ,
            // **`qa:run`, which the remote gate had as `strings:read`.** The scope's own definition
            // is "run QA checks and read release readiness", and readiness per locale is exactly
            // what this returns. A connection scoped only to read strings loses this tool, which is
            // the narrowing the scope was written to make.
            scopes = setOf(TokenScope.QA_RUN),
            input = AgentSchema.obj("project" to PROJECT_ARGUMENT),
            transports = setOf(Transport.LOCAL, Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "list_keys",
            summary = "List translation keys and their current values for one locale. Paged: the " +
                "response says how many match and carries a cursor while more remain.",
            safety = ToolSafety.READ,
            scopes = setOf(TokenScope.STRINGS_READ),
            // **Filters, because the alternative is paging a project to find four strings.** Both
            // servers took a locale and nothing else and then truncated at a hundred with no total
            // and no cursor, so an agent on a 5,000-key project saw 2% of the work and was told
            // nothing about the rest.
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "locale" to AgentSchema.string("Target locale, e.g. de."),
                "state" to AgentSchema.string(
                    "Only keys whose target is in this state.",
                    values = listOf("EMPTY", "TRANSLATED", "APPROVED"),
                ),
                "review" to AgentSchema.string(
                    "Only keys whose target sits at this point in review.",
                    values = listOf("NONE", "PENDING", "CHANGES_REQUESTED", "APPROVED"),
                ),
                "stale" to AgentSchema.boolean(
                    "Only keys whose source changed after the translation was written.",
                    default = false,
                ),
                "tag" to AgentSchema.string("Only keys carrying this tag."),
                "search" to AgentSchema.string("Substring of the key name or its text."),
                "limit" to AgentSchema.integer(
                    "Keys per page.",
                    minimum = 1,
                    maximum = 500,
                    default = 100,
                ),
                "cursor" to AgentSchema.string("The nextCursor from a previous page."),
                required = listOf("locale"),
            ),
            transports = setOf(Transport.LOCAL, Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "translation_brief",
            summary = "Everything needed to translate one locale well: the style guide, the " +
                "glossary terms that occur in this text, the CLDR categories the language uses, " +
                "and for each string its developer context, length budget, placeholders, where it " +
                "appears in the code, and what the target holds now. Free, and spends no credits.",
            safety = ToolSafety.READ,
            scopes = setOf(TokenScope.STRINGS_READ, TokenScope.GLOSSARY_READ),
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "locale" to AgentSchema.string("Target locale, e.g. ru."),
                "state" to AgentSchema.string(
                    "Only strings whose target is in this state. EMPTY is what \"finish this " +
                        "language\" means.",
                    values = listOf("EMPTY", "TRANSLATED", "APPROVED"),
                ),
                "review" to AgentSchema.string(
                    "Only strings at this point in review. CHANGES_REQUESTED is what \"fix what " +
                        "review sent back\" means.",
                    values = listOf("NONE", "PENDING", "CHANGES_REQUESTED", "APPROVED"),
                ),
                "stale" to AgentSchema.boolean(
                    "Only strings whose source changed after the translation was written.",
                    default = false,
                ),
                "tag" to AgentSchema.string("Only strings carrying this tag."),
                "file" to AgentSchema.string("Only strings in this file, by path."),
                "ref_locales" to AgentSchema.string(
                    "Comma-separated sibling locales to show alongside, at most three. Reading the " +
                        "German is often faster than reading the English.",
                ),
                "limit" to AgentSchema.integer(
                    "Strings per page.",
                    minimum = 1,
                    maximum = 500,
                    default = 100,
                ),
                "cursor" to AgentSchema.string("The nextCursor from a previous page."),
                required = listOf("locale"),
            ),
            transports = setOf(Transport.LOCAL, Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "get_key",
            summary = "One key in every language, with its context and length limit.",
            safety = ToolSafety.READ,
            scopes = setOf(TokenScope.STRINGS_READ),
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "key" to AgentSchema.string("Key name."),
                required = listOf("key"),
            ),
            transports = setOf(Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "search_strings",
            summary = "Find keys whose name or text matches a query, in one locale.",
            safety = ToolSafety.READ,
            scopes = setOf(TokenScope.STRINGS_READ),
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "locale" to AgentSchema.string("Target locale, e.g. de."),
                "query" to AgentSchema.string("What to look for, in key names and text."),
                required = listOf("locale", "query"),
            ),
            transports = setOf(Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "locale_info",
            // The local description is kept in full, including the writing direction it promises.
            // Neither server returned one — spec §3.2 says keep the promise and add the field rather
            // than weaken the description, and P2 is where the field lands.
            summary = "The CLDR plural categories a locale actually uses, and its writing direction.",
            safety = ToolSafety.READ,
            // Free to anyone. A model inventing Russian plural categories is the single most
            // expensive mistake on this surface, and gating the answer behind a scope would leave
            // the connections most likely to guess without it.
            scopes = emptySet(),
            input = AgentSchema.obj(
                "locale" to AgentSchema.string("A BCP-47 tag, e.g. ru."),
                required = listOf("locale"),
            ),
            transports = setOf(Transport.LOCAL, Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "list_glossary",
            summary = "The project's glossary: terms, their required translations, and the " +
                "do-not-translate list.",
            safety = ToolSafety.READ,
            scopes = setOf(TokenScope.GLOSSARY_READ),
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "locale" to AgentSchema.string("Target locale. Omit for every language's terms."),
            ),
            transports = setOf(Transport.LOCAL, Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "run_qa",
            summary = "Run the QA checks. Read-only, free, and the same findings the dashboard shows.",
            safety = ToolSafety.READ,
            scopes = setOf(TokenScope.QA_RUN),
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "locale" to AgentSchema.string("Limit to one locale."),
            ),
            transports = setOf(Transport.LOCAL, Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "estimate_cost",
            summary = "What a machine-translation batch would cost in credits. Free, and changes " +
                "nothing.",
            safety = ToolSafety.READ,
            // **`strings:read`, not `mt:spend`.** Spec §3.2's table says `mt:spend`, and both the
            // shipped gate and the spec's own definition of a scope disagree with it: pricing a
            // batch reserves nothing, and a connection that cannot afford to spend is exactly the
            // one that needs to find out what something would cost before asking a human. Gating
            // the estimate behind the spend scope would leave it able to run a batch it could not
            // price.
            scopes = setOf(TokenScope.STRINGS_READ),
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "locale" to AgentSchema.string("Target locale, e.g. de."),
                "provider" to AgentSchema.string("Provider id, e.g. deepl."),
                required = listOf("locale", "provider"),
            ),
            transports = setOf(Transport.REMOTE),
        ),
        AgentToolSpec(
            name = "pull_strings",
            summary = "Every string in one locale, as entries. No files: the caller writes them " +
                "out itself.",
            safety = ToolSafety.READ,
            // `files:write` rather than `strings:read`: it is the export half of the file pair, and
            // a credential scoped to read strings is not the same as one trusted with file sync.
            scopes = setOf(TokenScope.FILES_WRITE),
            minimumRole = Role.TRANSLATOR,
            input = AgentSchema.obj(
                "project" to PROJECT_ARGUMENT,
                "file" to AgentSchema.string("File path, as the project lists it."),
                "locale" to AgentSchema.string("Locale to read."),
                required = listOf("file", "locale"),
            ),
            transports = setOf(Transport.REMOTE),
        ),
    )
}
